import {
  Alert,
  Box,
  Card,
  Chip,
  Collapse,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { foyerApi, type ClientErrorGroup } from "../http/foyer";

const APPS: Array<{ key: string; label: string }> = [
  { key: "", label: "All apps" },
  { key: "tv", label: "TV" },
  { key: "cms", label: "CMS" },
  { key: "guest", label: "Guest companion" },
  { key: "family", label: "Relatives portal" },
  { key: "relative", label: "Call answer page" },
];
const APP_LABEL = Object.fromEntries(APPS.map((a) => [a.key, a.label]));

const short = (release: string) =>
  /^[0-9a-f]{12,}$/i.test(release) ? release.slice(0, 7) : release;

/**
 * Uncaught JS errors from every Foyer front-end, grouped by fingerprint.
 * Nenda staff only (canManageSettings on the route and the nav item).
 * Per-screen TV logs live in the device dialog; this page answers "which
 * errors are happening across all customers, and since which release".
 */
export default function ClientErrorsPage() {
  const [days, setDays] = useState(7);
  const [app, setApp] = useState("");
  const [groups, setGroups] = useState<ClientErrorGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setGroups(null);
    setError(null);
    foyerApi
      .clientErrors(days, app || undefined)
      .then((r) => live && setGroups(r.groups))
      .catch(
        (e) => live && setError(e instanceof Error ? e.message : String(e))
      );
    return () => {
      live = false;
    };
  }, [days, app]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Overview"
        title="Client errors"
        subtitle="Uncaught errors from the TV, CMS, guest, relatives and call pages, grouped. Counts are a lower bound: each page reports a repeat at most once per 10 minutes."
      />

      <Stack direction="row" spacing={2}>
        <TextField
          select
          size="small"
          label="App"
          value={app}
          onChange={(e) => setApp(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {APPS.map((a) => (
            <MenuItem key={a.key} value={a.key}>
              {a.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Window"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          {[1, 7, 30].map((d) => (
            <MenuItem key={d} value={d}>
              {d === 1 ? "Today (UTC)" : `Last ${d} days`}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && <Alert severity="error">Could not load: {error}</Alert>}
      {groups && groups.length === 0 && (
        <Alert severity="success">No client errors in this window.</Alert>
      )}
      {!groups && !error && (
        <Typography color="text.secondary">Loading…</Typography>
      )}

      {groups && groups.length > 0 && (
        <Card variant="outlined">
          {groups.map((g, i) => (
            <Fragment key={g.fingerprint}>
              <Box
                role="button"
                tabIndex={0}
                data-testid="client-error-group"
                onClick={() =>
                  setOpen(open === g.fingerprint ? null : g.fingerprint)
                }
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  setOpen(open === g.fingerprint ? null : g.fingerprint)
                }
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  borderTop: i ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="baseline">
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ minWidth: 56, textAlign: "right" }}
                  >
                    {g.count}
                  </Typography>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", wordBreak: "break-word" }}
                    >
                      {g.message}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}
                    >
                      <Chip size="small" label={APP_LABEL[g.app] ?? g.app} />
                      {g.kind !== "error" && (
                        <Chip size="small" variant="outlined" label={g.kind} />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {g.days} day{g.days === 1 ? "" : "s"} · last{" "}
                        {new Date(g.lastSeenAt).toLocaleString()} · since
                        release {short(g.firstRelease)}
                        {g.lastRelease !== g.firstRelease &&
                          `, still in ${short(g.lastRelease)}`}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
              <Collapse in={open === g.fingerprint} unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Path {g.lastPath}
                    {g.lastUserAgent && ` · ${g.lastUserAgent}`}
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      mt: 1,
                      p: 1,
                      fontSize: 11,
                      overflow: "auto",
                      maxHeight: 280,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    {g.stack ?? "No stack trace was reported."}
                  </Box>
                </Box>
              </Collapse>
            </Fragment>
          ))}
        </Card>
      )}
    </PageContainer>
  );
}
